// @ts-strict-ignore
import {
  isEventBasedEntry,
  isFormulaEntry,
  MetricEntry,
  MetricEntryBehavior,
} from 'common/query-builder/query-entry-section/types';
import {MetricBehavior, MetricMeasurement} from 'common/report/insights/util/types';
import {BehaviorShowClause, ShowClause} from 'common/report/insights/models/show-clause';
import {ShowSection} from 'common/report/insights/models/section';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {
  convertPropertySelectiontoBookmarkProperty,
  createRetentionMetricShowClause,
  getMetricEntryBehavior,
} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {MetricTypeIcons} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/types/constants';
import {SvgIconType} from 'common/components/svg-icon';
import {Metric} from 'app_api/projects/metrics/types';

import {isFormulaShowClause} from '../models/utils';
import {ReportSectionsMetadata, ShowClause as ShowClauseType} from '../util/types';
import {FilterClause} from '../models/filter-clause';
import {BehaviorMetric, InsightsQueryError} from './types';
import {createFunnelMetricShowClause, getMetricEntryMeasurement} from './events';

/**
 * Transforms clauses in a bookmark to conform to the MetricEntry type
 * The Insights QB is based only on this type of project as of the
 * First Class Metrics project
 */
export function getMetricEntriesFromShowClauses({
  showClauses,
  dateUnit,
  queryError,
  reportSectionsMetadata,
}: {
  showClauses: ShowClauseType[];
  behaviorsResource?: BehaviorsResource;
  metricsResource?: MetricsResource;
  dataGroups?: DataGroup[];
  queryError?: InsightsQueryError;
  dateUnit?: TimeUnit;
  reportSectionsMetadata?: ReportSectionsMetadata;
}): MetricEntry[] {
  const entries: MetricEntry[] = [];

  const formulaErrorByIdx = new Map<number, string>(
    (queryError?.formula_errors || []).map(({index, message}) => [index, message]),
  );

  let formulaIdx = 0;
  showClauses.forEach((clause: ShowClauseType, index: number) => {
    /** @todo handle formulas */
    if (isFormulaShowClause(clause)) {
      const error = formulaErrorByIdx.get(formulaIdx++);
      const formulaProps: MetricEntry = {
        _idx: clause._idx,
        type: BookmarkTypes.TopLevelMetricType.NewFormulaEntry,
        id: clause.id,
        name: clause.name,
        error,
        behavior: null,
        measurement: clause.measurement,
        definition: clause.definition,
        display: clause.display,
        isHidden: clause.isHidden ?? false,
        isExpanded: clause.isExpanded ?? false,
        hasUnsavedChanges: clause.hasUnsavedChanges ?? false,

        // only populate referenced entries when available
        referencedMetrics: clause.referencedMetrics
          ? getMetricEntriesFromShowClauses({
              showClauses: clause.referencedMetrics,
              dateUnit,
              queryError,
              reportSectionsMetadata,
            })
          : undefined,
      };
      entries.push(formulaProps);
      return;
    }

    const props: MetricEntry = {
      _idx: clause._idx,
      type: BookmarkTypes.TopLevelMetricType.NewMetricEntry,
      id: clause.id,
      name: clause.name,
      userNamed: clause.userNamed,
      isHidden: clause.isHidden ?? false,
      isExpanded: clause.isExpanded ?? false,
      hasUnsavedChanges: clause.hasUnsavedChanges ?? false,
      behavior: {
        ...getMetricEntryBehavior(clause.behavior),
        hasUnsavedChanges: clause.behavior.hasUnsavedChanges ?? false,
      },
      measurement: getMetricEntryMeasurement({
        clause: clause as BehaviorMetric,
        index,
        dateUnit,
        reportSectionsMetadata,
      }),
      display: clause.display,
    };

    entries.push(props);
  });

  return entries;
}

/**
 * Transforms metric entries to show clauses within a show section
 */
export function getShowSectionFromMetricsEntries({
  entries,
  dataGroupId,
  attributionSelection,
}: {
  entries: MetricEntry[];
  dataGroupId?: string;
  attributionSelection?: number | BehaviorShowClause[`measurement`][`actionMode`];
}): ShowSection {
  if (entries.length === 0) {
    return ShowSection.createShowSection({
      behavior: {
        type: BookmarkTypes.MetricType.Event,
        // Cleanup: Missing resourceType
        resourceType: undefined,
      },
      measurement: {
        // Data group id goes in measurement except for cohort / profile entries
        // which go in behavior.
        dataGroupId,
      },
    });
  }

  const showClauses = entries.map((entry) => {
    if (isFormulaEntry(entry)) {
      throw new Error(`Invalid entry type`);
    }

    const commonMetricFields: Partial<BookmarkTypes.BehaviorShowClause | BookmarkTypes.FormulaShowClause> = {
      _idx: entry._idx,
      id: entry.id,
      name: entry.name,
      userNamed: entry.userNamed,
      isHidden: entry.isHidden ?? false,
      isExpanded: entry.isExpanded ?? false,
      hasUnsavedChanges: entry.hasUnsavedChanges ?? false,
      display: entry.display,
    };

    if (entry.type === BookmarkTypes.TopLevelMetricType.NewFormulaEntry) {
      const metric: BookmarkTypes.FormulaShowClause = {
        ...commonMetricFields,
        type: BookmarkTypes.TopLevelMetricType.Formula,
      };

      metric.definition = entry.definition;
      metric.display = entry.display;
      metric.measurement = entry.measurement;

      // only populate referenced entries when available
      if (entry.referencedMetrics) {
        metric.referencedMetrics = getShowSectionFromMetricsEntries({
          entries: entry.referencedMetrics,
        }).clauses as Array<BookmarkTypes.BehaviorShowClause>;
      }

      return ShowClause.createShowClause(metric);
    }

    let metricBehavior: MetricBehavior | null;
    let metricMeasurement: MetricMeasurement | null;

    /** @todo (pablo) this whole block of code will be simplified/updated to work with metrics only */
    if (entry.behavior.type === BookmarkTypes.MetricType.Funnel) {
      const clause = createFunnelMetricShowClause(entry, attributionSelection, true) as BehaviorShowClause;
      metricBehavior = clause.behavior;
      metricMeasurement = clause.measurement;
    } else if (
      entry.behavior.type === BookmarkTypes.MetricType.Retention ||
      entry.behavior.type === BookmarkTypes.MetricType.RetentionFrequency
    ) {
      const clause = createRetentionMetricShowClause(
        entry.behavior.type,
        entry,
        attributionSelection,
        true,
      ) as BehaviorShowClause;
      metricBehavior = clause.behavior;
      metricMeasurement = clause.measurement;
    } else {
      metricBehavior = transformMetricEntryToMetricBehavior(entry.behavior);

      metricMeasurement = {
        math: entry.measurement.aggregationOperator || BookmarkTypes.PRIMITIVE_MATH_TYPES.TOTAL,
        perUserAggregation:
          (entry.measurement.aggregationOperatorPerUser as BookmarkTypes.PER_USER_AGGREGATIONS) || null,
        rolling: entry.measurement.rolling || null,
        cumulative: entry.measurement.cumulative || false,

        ...(entry.measurement.countUsersOnceType ? {segmentMethod: entry.measurement.countUsersOnceType} : {}),

        // converts a property aggregation to a valid bookmark format
        ...(entry.measurement.property
          ? {
              property: convertPropertySelectiontoBookmarkProperty(entry.measurement.property),
            }
          : {}),

        // attribution breakdowns only happen for event based metrics
        ...(isEventBasedEntry(entry) && entry.measurement.multiAttribution
          ? {multiAttribution: entry.measurement.multiAttribution}
          : {}),
      };
    }

    // Handle data group id.
    // Data group id goes in measurement except for cohort / profile entries
    // which go in behavior.
    if (isEventBasedEntry(entry)) {
      metricMeasurement.dataGroupId = entry.measurement.dataGroupId ?? dataGroupId;
    } else {
      metricBehavior.dataGroupId = metricBehavior.dataGroupId ?? dataGroupId;
    }

    // populate final metric
    const metric: BookmarkTypes.BehaviorShowClause = {
      ...commonMetricFields,
      type: BookmarkTypes.TopLevelMetricType.Metric,
      measurement: metricMeasurement,
      display: entry.display,
      behavior: {
        ...metricBehavior,
        id: entry.behavior.id,
        type: metricBehavior.type,
        name: entry.behavior.name,
        hasUnsavedChanges: entry.behavior.hasUnsavedChanges ?? false,
      },
    };

    return ShowClause.createShowClause(metric);
  });

  return new ShowSection(showClauses);
}

const BEHAVIOR_TYPE_RESOURCE_TYPE_MAPPING = {
  [BookmarkTypes.MetricType.People]: BookmarkTypes.InsightsResourceType.People,
  [BookmarkTypes.MetricType.Cohort]: BookmarkTypes.InsightsResourceType.Cohorts,
};

export function transformMetricEntryToMetricBehavior(behavior: MetricEntryBehavior): MetricBehavior {
  const subBehaviors = behavior.behaviors ?? [];
  const filters = behavior.filters ?? [];
  const serializeFilter = (filter) => FilterClause.fromPropertyFilter(filter).serialize();

  return {
    id: behavior.id,
    type: behavior.type,
    name: behavior.name,
    renamed: behavior.renamed,
    resourceType:
      behavior.resourceType ??
      BEHAVIOR_TYPE_RESOURCE_TYPE_MAPPING[behavior.type] ??
      BookmarkTypes.InsightsResourceType.Events,
    dataGroupId: behavior.dataGroupId,
    customBucket: behavior.customBucket,
    filters: filters.map(serializeFilter),
    filtersDeterminer: behavior.filtersDeterminer ?? `all`,
    behaviors: subBehaviors.map((subBehavior) => {
      return {
        id: subBehavior.id,
        type: subBehavior.type as BookmarkTypes.SubBehavior[`type`],
        name: subBehavior.name,
        renamed: subBehavior.renamed,
        filters: subBehavior.filters?.map(serializeFilter),
        filtersDeterminer: subBehavior.filtersDeterminer ?? `all`,
      };
    }),

    // passes any inline cohort definition for cohort metrics
    ...(behavior.raw_cohort ? {raw_cohort: behavior.raw_cohort} : {}),
  };
}

export function getIconForSavedMetric(metric: Metric): SvgIconType {
  if (metric.type === `formula`) {
    return `formula`;
  }
  const metricType = metric.definition?.behavior?.type || ``;
  // Fallback to `data-metric` if we can't tell the type for some reason.
  return metricType in MetricTypeIcons ? MetricTypeIcons[metricType as keyof typeof MetricTypeIcons] : `data-metric`;
}
